import { useEffect } from 'react';
import {
  queryOptions,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import {
  loadPdfDocument,
  type PdfDocumentProxy,
} from './runtime';

export const viewerPdfQueryKeys = {
  document: (fileUrl: string) =>
    [
      'ironview-interactive-viewer',
      'pdf-document',
      fileUrl,
    ] as const,
};

type ViewerPdfQueryCache = ReturnType<QueryClient['getQueryCache']> & {
  __viewerPdfDestroyHookInstalled?: boolean;
};

export const installViewerPdfDocumentCleanup = (
  queryClient: QueryClient,
) => {
  const cache = queryClient.getQueryCache() as ViewerPdfQueryCache;
  if (cache.__viewerPdfDestroyHookInstalled) return;
  cache.__viewerPdfDestroyHookInstalled = true;

  // This subscription intentionally lives for the QueryCache lifetime.
  // Unsubscribing when one viewport unmounts would disable cleanup for
  // other viewers using the same QueryClient.
  cache.subscribe((event) => {
    if (event.type !== 'removed') return;
    const key = event.query.queryKey;
    if (
      !Array.isArray(key) ||
      key[0] !== 'ironview-interactive-viewer' ||
      key[1] !== 'pdf-document'
    ) {
      return;
    }
    const document = event.query.state.data as
      | PdfDocumentProxy
      | undefined;
    try {
      void document?.destroy?.();
    } catch {
      // PDF.js can throw when a worker was already destroyed.
    }
  });
};

export const viewerPdfDocumentQueryOptions = (
  fileUrl: string | null,
  loader: (fileUrl: string) => Promise<PdfDocumentProxy> =
    loadPdfDocument,
) =>
  queryOptions({
    queryKey: viewerPdfQueryKeys.document(fileUrl ?? ''),
    enabled: Boolean(fileUrl),
    queryFn: () => {
      if (!fileUrl) throw new Error('Missing PDF file URL');
      return loader(fileUrl);
    },
    staleTime: 60_000,
    gcTime: 0,
    // A loaded PDF document is immutable for the lifetime of its viewport.
    // Refetching on focus/reconnect can retry an expired signed URL and replace
    // a healthy document while the user is editing the layout.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const useViewerPdfDocument = (
  fileUrl: string | null,
  loader: (fileUrl: string) => Promise<PdfDocumentProxy> =
    loadPdfDocument,
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    installViewerPdfDocumentCleanup(queryClient);
  }, [queryClient]);

  return useQuery(viewerPdfDocumentQueryOptions(fileUrl, loader));
};
